import { useEffect, useState } from 'react';
import { audit } from '@/api/endpoints';
import type { AuditEntry } from '@/types';
import { Pagination, usePaginated } from '@/components/Pagination';

function chipClass(type: string): string {
  if (type.startsWith('AI_')) return 'pill-ai';
  if (type === 'SIGNATURE') return 'pill-marsz';
  if (type === 'DELETE') return 'pill-red';
  if (type === 'CREATE') return 'pill-green';
  if (type === 'AUTHORITY_CHANGE') return 'pill-amber';
  return 'pill-water';
}

export function Audit() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [types, setTypy] = useState<string[]>([]);
  const [verify, setVerify] = useState<{ ok: boolean; count: number; dzisiaj: number } | null>(null);
  const [filtr, setFiltr] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      audit.log(filtr || undefined),
      audit.types(),
      audit.verify(),
    ])
      .then(([log, types, ver]) => { setEntries(log); setTypy(types); setVerify(ver); setLoading(false); })
      .catch(e => { setErr(String(e)); setLoading(false); });
  }, [filtr]);

  const { pagedItems, page, pageSize, setPage, setPageSize, total } = usePaginated(entries, { storageKey: 'audit', defaultPageSize: 50 });

  if (err) return <div className="text-red-500">Błąd: {err}</div>;
  if (loading) return <div className="text-ink-400">Ładowanie…</div>;

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Log audytu</h1>
          <div className="text-ink-400 text-[13px]">
            Nienaruszalny ślad: logowania, zmiany decydenta, podpisy, AI · łańcuch skrótów SHA-256
          </div>
        </div>
        <div>
          <span className={'pill ' + (verify?.ok ? 'pill-green' : 'pill-red')}>
            {verify?.ok ? '✓ Łańcuch hashów OK' : '✗ BŁĄD łańcucha'}
          </span>
        </div>
      </header>

      <div className="grid grid-cols-4 gap-3.5 mb-6">
        <div className="kpi"><div className="absolute inset-y-0 left-0 w-[3px] bg-water-500"/><div className="kpi-label">Zdarzenia dziś</div><div className="kpi-value">{verify?.dzisiaj ?? 0}</div></div>
        <div className="kpi"><div className="absolute inset-y-0 left-0 w-[3px] bg-water-500"/><div className="kpi-label">Wszystkie zapisy</div><div className="kpi-value">{verify?.count ?? 0}</div></div>
        <div className="kpi"><div className="absolute inset-y-0 left-0 w-[3px] bg-green-500"/><div className="kpi-label">Integralność</div><div className="kpi-value font-mono text-[18px]">{verify?.ok ? 'SHA-256 ✓' : 'BŁĄD'}</div></div>
        <div className="kpi"><div className="absolute inset-y-0 left-0 w-[3px] bg-ai-500"/><div className="kpi-label">Append-only</div><div className="kpi-value font-mono text-[18px]">WORM</div></div>
      </div>

      <div className="grid grid-cols-[220px_1fr] gap-4">
        <div className="card self-start">
          <div className="card-head"><div className="card-title">Filtry</div></div>
          <div className="card-body !py-2.5 !px-3.5">
            <div className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold mb-1.5">Typ zdarzenia</div>
            <button
              onClick={() => setFiltr('')}
              className={'pill block mb-1 w-full text-left cursor-pointer ' + (filtr === '' ? 'pill-water' : '')}
            >
              Wszystkie
            </button>
            {types.map(t => (
              <button
                key={t}
                onClick={() => setFiltr(t)}
                className={'pill block mb-1 w-full text-left cursor-pointer text-[10px] ' + (filtr === t ? 'pill-water' : '')}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="card-head"><div className="card-title">Ostatnie {entries.length} zdarzeń</div></div>
          <div className="card-body !p-0">
            {entries.length === 0 && <div className="text-ink-400 p-5 text-center">Brak zdarzeń dla wybranego filtru.</div>}
            {pagedItems.map(e => {
              const time = new Date(e.eventTime);
              const label = time.toLocaleDateString('pl-PL', { month: '2-digit', day: '2-digit' })
                + ' ' + time.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
              const firstLetter = (e.actorLogin ?? '?').substring(0, 1).toUpperCase();
              return (
                <div key={e.id} className="flex gap-3 px-3 py-2.5 border-b border-ink-100 last:border-b-0 items-start hover:bg-ink-50">
                  <div className="font-mono text-[11px] text-ink-400 w-[80px] shrink-0 pt-0.5">{label}</div>
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#1e3a8a] to-water-500 text-white text-[10px] font-semibold grid place-items-center shrink-0">
                    {firstLetter}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div>
                      <span className={'pill mr-1.5 text-[10px] ' + chipClass(e.eventType)}>{e.eventType}</span>
                      <span className="font-medium">{e.description}</span>
                    </div>
                    <div className="text-[11px] text-ink-400 mt-0.5">
                      {e.actorLogin && <span className="font-mono">{e.actorLogin}</span>}
                      {/* Sprawstwo w trybie „pracuj jako": wpis jest zapisany na konto użytkownika,
                          ale wykonał go administrator — bez tego znacznika audyt sugerowałby, że
                          zrobił to sam użytkownik. */}
                      {e.impersonatorLogin && (
                        <span className="pill text-[10px] bg-amber-100 text-amber-800 ml-1.5"
                              title={`Akcję wykonał ${e.impersonatorLogin}, pracując jako ${e.actorLogin}`}>
                          przez {e.impersonatorLogin}
                        </span>
                      )}
                      {e.actorLogin && <span> · </span>}
                      {e.objectType && <span>{e.objectType}#{e.objectId} · </span>}
                      <span className="font-mono">hash: {e.currentHash.substring(0, 12)}…</span>
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="px-3 border-t border-ink-100">
              <Pagination total={total} page={page} pageSize={pageSize}
                          onPageChange={setPage} onPageSizeChange={setPageSize}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
